import {
    getProducts,
    bulkDeleteProduct,
    downloadAllProducts,
    saveProduct,
    downloadSampleProduct,
} from 'network/settings-product';
import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, ThemeType } from 'theme/jss-types';
import { Product, TableColumn } from './settings.types';
import {
    Button,
    message,
    Input,
    Dropdown,
    Menu,
    Tooltip,
} from 'antd';
import { productColumns } from './settings.constants';
import { addressBookStyles } from '../address-book/address-book.styles';
import {
    EditFilled,
    DeleteFilled,
    SearchOutlined,
    InfoCircleOutlined,
} from '@ant-design/icons';
import NewProduct from './new-product';
import DeleteProduct from './delete-product';
import { useTranslation } from 'react-i18next';
import { ReduxStore } from 'reducers/redux.types';
import GenericHoc from 'components/common/generic-hoc';
import { HocOptions } from 'components/common/generic-hoc.types';
import Paginator from 'components/common/paginator';
import Helper from 'library/Helper';
import { NextOrPrev, Pagination } from 'library/Types';
import ExcelUpload from './excel-upload';
import AsyncBatchModalService from '../../services/async-batch-modal.service';
import { useDebounce } from 'hooks/use-debounce';
import { productHeaders, productHeaderMapping } from './product-utils';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { NAVBAR_HEIGHT } from 'library/globals';
import { Master } from 'types/master-data-types';

interface ProductMappingProps extends StylesProps<ReturnType<typeof addressBookStyles>> {
    uiTheme: ThemeType,
    declaredCurrencyList: any[],
    master: Master,
}

const ProductMapping = (props: ProductMappingProps) => {
    const {
        classes,
        uiTheme,
        declaredCurrencyList,
        master,
    } = props;
    const { t } = useTranslation();
    const [products, setProducts] = React.useState<any>([]);
    const [loading, setLoading] = React.useState<boolean>(true);
    const [toggle, setToggle] = React.useState<boolean>(true);
    const [newVisible, setNewVisible] = React.useState<boolean>(false);
    const [product, setProduct] = React.useState<Product | undefined>();
    const [isDeleteModalVisible, setIsDeleteModalVisible] = React.useState<boolean>(false);
    const [searchString, setSearchString] = React.useState<string>('');
    const [deleteLoading, setDeleteLoading] = React.useState<boolean>(false);
    const [excelUploadVisible, setExcelUploadVisible] = React.useState<boolean>(false);
    const [downloading, setDownloading] = React.useState<boolean>(false);
    const [productSelected, setProductSelected] = React.useState<string[]>([]);
    const [pagination, setPagination] = React.useState<Pagination>({
        nextOrPrev: 'first',
        currentPageNumber: 1,
        resultPerPage: 20,
        isNextPresent: false,
        lastUpdatedAt: null,
    });
    // to be sent to excel upload modal
    const [parsedProductList, setParsedProductList] = React.useState<any>([]);

    const debouncedSearchString = useDebounce(searchString);


    const handlePagination = (nextOrPrev: NextOrPrev) => {
        const { currentPageNumber } = pagination;
        let newPageNumber = currentPageNumber;
        switch (nextOrPrev) {
            case 'first':
                newPageNumber = 1;
                break;
            case 'next':
                newPageNumber = currentPageNumber + 1;
                break;
            case 'prev':
                newPageNumber = currentPageNumber - 1;
                break;
            default:
                break;
        }
        const newPagination: Pagination = {
            ...pagination,
            nextOrPrev,
            currentPageNumber: newPageNumber,
        };
        setPagination(newPagination as Pagination);
        setToggle(!toggle);
    };

    const renderPaginator = () => {
        return (
            <Paginator
                currentPageNumber={pagination?.currentPageNumber}
                isNextPresent={pagination?.isNextPresent}
                onNextClick={() => handlePagination('next')}
                onPrevClick={() => handlePagination('prev')}
                onFirstClick={() => handlePagination('first')}
            />

        );
    };

    const bannerHeight = master?.config?.is_using_new_customer_portal ? 0 : 50;

    const loadProducts = async () => {
        if (debouncedSearchString && debouncedSearchString.length < 2) {
            return;
        }
        setLoading(true);
        const response = await getProducts({
            searchString: debouncedSearchString,
            pageNumber: pagination?.currentPageNumber,
            resultPerPage: pagination?.resultPerPage,
        });
        if (!response.isSuccess) {
            message.error(response.errorMessage);
        } else {
            setProducts(response?.data?.productList || []);
            const newPagination = {
                ...pagination,
                isNextPresent: response?.data?.isNextPresent,
            };
            setPagination(newPagination as Pagination);
        }
        setLoading(false);
    };

    React.useEffect(() => {
        loadProducts();
        setProductSelected([]);
    }, [debouncedSearchString, toggle]);

    const renderText = (text: string | number) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };

    const handleEditProduct = (row: Product) => {
        setProduct(row);
        setNewVisible(true);
    };

    const handleDeleteProduct = (row: Product) => {
        setProduct(row);
        setIsDeleteModalVisible(true);
    };

    const renderActions = (row: Product) => {
        return (
            <>
                <EditFilled
                    key={row.id}
                    style={{
                        color: uiTheme.primaryColor,
                        cursor: 'pointer',
                        marginRight: '10px',
                    }}
                    onClick={() => handleEditProduct(row)}
                />
                <DeleteFilled
                    key={row.id}
                    style={{
                        color: uiTheme.primaryColor,
                        cursor: 'pointer',
                    }}
                    onClick={() => handleDeleteProduct(row)}
                />
            </>
        );
    };

    const renderEllipsisText = (text: string) => {
        return (
            <div
                className={classes.cellValue}
                title={text}
                style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
                {text}
            </div>
        );
    };

    const renderColumn = (
        text: string,
        row: Product,
        column: string,
        index: number,
    ) => {
        switch (column) {
            case 'index': return renderText(((pagination.currentPageNumber - 1) * pagination.resultPerPage) + index + 1);
            case 'actions': return renderActions(row);
            case 'dimensions': return renderText(`${row.length} * ${row.width} * ${row.height}`);
            case 'dangerousGoodName': return renderEllipsisText(text);
            default: return renderText(text as string);
        }
    };

    const getWidth = (column: string) => {
        switch (column) {
            case 'index': return 60;
            case 'dangerousGoodUnNumber': return 100;
            case 'dangerousGoodName': return 220;
            default: return 160;
        }
    };

    const renderTitleWithTooltip = (title: string, tooltip: string) => {
        return (
            <span>
                {t(title)}
                <Tooltip title={t(tooltip)}>
                    <InfoCircleOutlined style={{ marginLeft: '5px' }} />
                </Tooltip>
            </span>
        );
    };

    const getColumnTitle = (column: string) => {
        switch (column) {
            case 'name': return t('product_name');
            case 'dimensions': return t('dimension');
            case 'dangerousGoodUnNumber': return renderTitleWithTooltip('un_number', 'un_number_info');
            default: return t(column);
        }
    };

    const renderColumns = () => {
        const columns = [...productColumns];
        if (declaredCurrencyList.length && !columns.some((col) => col.key === 'declaredCurrency')) {
            columns.push({
                key: 'declaredCurrency',
                pretty_name: 'Declared Currency',
            });
        }
        const mappedColumns: any = columns.map((column: TableColumn) => {
            return {
                key: column.key,
                title: getColumnTitle(column.key),
                dataIndex: column.key,
                ellipsis: true,
                bordered: false,
                width: getWidth(column.key),
                render: (text: string, row: any, idx: number) => renderColumn(text, row, column.key, idx),
            };
        });
        return mappedColumns;
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                columns={renderColumns()}
                loading={loading}
                dataSource={products}
                pagination={false}
                rowKey={(row: any) => (row.code)}
                rowSelection={{
                    selectedRowKeys: productSelected,
                    onSelect: (row: any, selected: any) => {
                        let newArray = [...productSelected];
                        if (selected && !newArray.includes(row.code)) {
                            newArray.push(row.code);
                        } else {
                            newArray = newArray.filter((item) => (item !== row.code));
                        }
                        setProductSelected(newArray);
                    },
                    onSelectAll: (selected: any, selectedRows: any) => {
                        const newArray = selectedRows.map((item: any) => (item.code));
                        setProductSelected(newArray);
                    },
                }}
                scroll={{
                    y: `calc(((100vh - ${NAVBAR_HEIGHT}px) - 110px) - 50px)`,
                }}
                style={{
                    height: `calc(
                        (((100vh - ${NAVBAR_HEIGHT}px) - 55px) - ${bannerHeight}px) )`,
                    background: '#ffffff',
                }}
            />
        );
    };

    const handleFormClose = (reload?: boolean) => {
        setNewVisible(false);
        setProduct(undefined);
        setIsDeleteModalVisible(false);
        if (reload) {
            setToggle(!toggle);
        }
    };

    const renderCreate = () => {
        if (!newVisible) {
            return null;
        }
        return (
            <NewProduct
                declaredCurrencyList={declaredCurrencyList}
                product={product}
                onClose={(reload) => handleFormClose(reload)}
            />
        );
    };

    const renderDelete = () => {
        if (!isDeleteModalVisible) {
            return null;
        }
        return (
            <DeleteProduct
                product={product}
                onClose={(reload) => handleFormClose(reload)}
            />
        );
    };

    const renderDownload = () => {
        return (
            <Button
                type="primary"
                loading={downloading}
                className={classes.downloadBtn}
            >
                {t('download')}
            </Button>
        );
    };

    const handleDownload = () => {
        if (productSelected && !productSelected.length) {
            message.warning('Please select at least one product to download');
            return;
        }

        const selectedProducts = products?.filter((_product: any) => productSelected.includes(_product.code));
        const data = selectedProducts?.map((_product: any) => {
            const row: any = {};
            productHeaders?.forEach((header) => {
                const key = productHeaderMapping[header];
                row[header] = _product[key] || '';
            });
            return row;
        });

        setDownloading(true);
        Helper.downloadXLSXFile(data, productHeaders, 'products.xlsx', 'Products');
        setDownloading(false);
    };

    const handleDownloadAll = async () => {
        setDownloading(true);
        const fileBuffer = await downloadAllProducts();

        const fileName = fileBuffer.filename;
        if (fileBuffer.isSuccess) {
            Helper.downloadFileData(
                fileBuffer.data,
                fileName || 'product_master.xlsx',
                true,
            );
            message.success('Downloaded Successfully');
        } else {
            message.error(fileBuffer.errorMessage);
        }

        setDownloading(false);
    };

    const downloadProductMenu = (
        <Menu>
            <Menu.Item
                key="profile"
                onClick={() => handleDownloadAll()}
            >
                All
            </Menu.Item>
            <Menu.Item
                key="settings"
                disabled={!(productSelected || []).length}
                onClick={() => handleDownload()}
            >
                {t('selected')}
            </Menu.Item>
        </Menu>
    );

    const renderDownloadDropdown = () => {
        return (
            <Dropdown
                overlay={downloadProductMenu}
                trigger={['click']}
            >
                {renderDownload()}
            </Dropdown>
        );
    };

    const handleSearchQuery = (e: any) => {
        const newPagination = {
            ...pagination,
            currentPageNumber: 1,
        };
        setPagination(newPagination as Pagination);
        setSearchString(e.target.value);
    };

    const renderFilter = () => {
        return (
            <div className={classes.filter}>
                <Input
                    className={classes.searchInput}
                    type="search"
                    value={searchString}
                    onChange={(e: any) => handleSearchQuery(e)}
                    placeholder={t('search_for_product')}
                    suffix={<SearchOutlined />}
                />
            </div>
        );
    };

    const renderLeftFilters = () => {
        return (
            <div className={classes.leftFilters}>
                {renderFilter()}
            </div>
        );
    };

    const handleDeleteBulk = async (e: any, productSelected_: string[]) => {
        if (productSelected_ && !productSelected_.length) {
            return;
        }
        Helper.stops(e);
        setDeleteLoading(true);
        const result = await bulkDeleteProduct({ codes: productSelected_ });
        if (result.data.success) {
            setToggle(!toggle);
            message.success('Products Deleted Successfully');
        } else {
            message.error('Unable to delete the selected products');
        }
        setDeleteLoading(false);
    };

    const renderDeleteButton = () => {
        return (
            <Button
                type="primary"
                loading={deleteLoading}
                className={classes.downloadBtn}
                disabled={!(productSelected || []).length}
                onClick={(e) => handleDeleteBulk(e, productSelected)}
            >
                {t('delete_product')}
            </Button>
        );
    };

    const openCreateProductModal = () => {
        setNewVisible(true);
    };

    const onCloseDrawer = () => {
        if (!AsyncBatchModalService.getInstance().isVisible()) {
            setExcelUploadVisible(false);
            setParsedProductList([]);
        }
    };

    const getProductList = (data: any[]) => {
        const headers = data[0];
        const slicedData = data.slice(1)?.filter((item) => !Object.values(item).every((val) => val === null || val === ''));
        const newData: any[] = slicedData.map((item) => {
            const returnObj: Record<string, any> = {};
            Object.keys(item).forEach((key: string) => {
                let keyUpdate = headers[key];
                keyUpdate = productHeaderMapping[keyUpdate];
                returnObj[keyUpdate] = item[key];
            });
            return returnObj;
        });
        return newData;
    };

    const transformData = (data: any[]) => {
        const newData: any = {};
        newData.productList = getProductList(data[0]);
        return newData;
    };

    const handleParsedData = async (data: any[]) => {
        if (!data || !data.length) return;
        const slicedData = transformData(data);
        setParsedProductList(slicedData.productList);
    };

    const renderExcelUpload = () => {
        if (!excelUploadVisible) return null;

        return (
            <ExcelUpload
                onClose={onCloseDrawer}
                handleParsedData={handleParsedData}
                parsedData={parsedProductList}
                headerMapping={productHeaderMapping}
                header="Product Upload"
                apiCallFn={saveProduct}
                downloadSample={downloadSampleProduct}
            />
        );
    };

    const newProductMenu = (
        <Menu>
            <Menu.Item
                key="profile"
                onClick={() => openCreateProductModal()}
            >
                {t('single_product')}
            </Menu.Item>
            <Menu.Item
                key="settings"
                onClick={() => {
                    if (AsyncBatchModalService.getInstance().isVisible()) {
                        message.warning('Please let current upload to complete and close the modal first');
                        return;
                    }
                    setExcelUploadVisible(true);
                }}
            >
                {t('upload_excel_product')}
            </Menu.Item>
        </Menu>
    );


    const renderNewProduct = () => {
        return (
            <Button
                type="primary"
                className={classes.newAddressBtn}
            >
                {t('new_product')}
            </Button>
        );
    };

    const renderCreateProductDropdown = () => {
        return (
            <Dropdown
                overlay={newProductMenu}
                trigger={['click']}
            >
                {renderNewProduct()}
            </Dropdown>
        );
    };

    const renderRightFilters = () => {
        return (
            <div className={classes.rightFilters}>
                {renderDeleteButton()}
                {renderDownloadDropdown()}
                {renderCreateProductDropdown()}
            </div>
        );
    };

    const renderExtraFilters = () => {
        return (
            <div className={classes.extraFilters}>
                {renderLeftFilters()}
                {renderRightFilters()}
            </div>
        );
    };

    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                {renderExtraFilters()}
            </div>
        );
    };

    const renderFooter = () => {
        return (
            <div className={classes.footer}>
                {renderPaginator()}
            </div>
        );
    };

    return (
        <div className={classes.main} style={{ padding: '0px' }}>
            {renderFilters()}
            {renderTable()}
            {renderCreate()}
            {renderDelete()}
            {renderExcelUpload()}
            {renderFooter()}
        </div>
    );
};
const mapStateToProps = (state: ReduxStore) => {
    const { master } = state;
    const { parts_to_show } = master;
    const declaredCurrenciesList = Array.isArray(parts_to_show?.declared_currency_list)
        ? parts_to_show.declared_currency_list
        : [];

    return {
        uiTheme: state.uiTheme,
        declaredCurrencyList: declaredCurrenciesList,
        master: state.master,
    };
};
const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};


export default withStyles(addressBookStyles)(GenericHoc(hocConfig)(ProductMapping));
